import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { injectIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import Spinner from '../../util/spinner-shim'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import AppButton from '../partials/Navigation/AppButton'
import StatusSave from '../partials/StatusSave'
import AppStore from '../../stores/AppStore'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import styles from '../../../css/home.module.css'
import withAppStore from '../../util/withAppStore'

const sortString = (group) => `${group.name || ''}-${group.deviceGroup || group.device_group || ''}-${group.icon || ''}`

function UserGroupsPage (props) {
  const navigate = useNavigate()
  const { customerID, userID } = useParams()
  const [groups, setGroups] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [statusMessageIsVisible, setStatusMessageIsVisible] = React.useState(false)
  const [statusOutcome, setStatusOutcome] = React.useState(undefined)
  const [statusMessage, setStatusMessage] = React.useState(undefined)

  const goTo = React.useCallback((x) => navigate(x), [navigate])
  const goBack = React.useCallback(() => {
    if (customerID && userID) {
      goTo(`/${customerID}/settings/users/${userID}`)
      return
    }
    const { id: fallbackCustomerID } = AppStore.getCustomer() || {}
    goTo(`/${fallbackCustomerID}/settings/users`)
  }, [goTo, customerID, userID])

  const buildGroupState = React.useCallback(() => {
    const userGroups = AppStore.getUserGroups() || []
    const activeGroupIds = new Set(
      userGroups
        .filter((userGroup) => userGroup && userGroup.is_active)
        .map((userGroup) => String(userGroup.group?.id ?? userGroup.group_id))
        .filter(Boolean)
    )

    const { applications: customerGroups = [] } = AppStore.getCustomer() || {}
    const fallbackGroups = userGroups
      .map((userGroup) => userGroup.group)
      .filter(Boolean)
    const baseGroups = (Array.isArray(customerGroups) && customerGroups.length)
      ? customerGroups
      : fallbackGroups

    const seen = new Set()
    const uniqueGroups = baseGroups.filter((group) => {
      const key = group.id ?? group.groupID ?? group.name ?? group.deviceGroup
      if (!key) return true
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    const nextGroups = uniqueGroups
      .sort((a, b) => sortString(a).localeCompare(sortString(b)))
      .map((group) => {
        const groupId = group.id ?? group.groupID
        return {
          ...group,
          checked: activeGroupIds.has(String(groupId)),
        }
      })

    setGroups(nextGroups)
    setLoading(false)
  }, [])

  const getGroups = React.useCallback(() => {
    if (customerID) {
      AppDispatcher.dispatch({
        action: 'get-customer',
        customerID,
        callback: buildGroupState,
      })
    }

    AppDispatcher.dispatch({
      action: 'get-user-groups',
      params: {
        all: true,
        customerID,
        includeGroup: true,
        userID,
      },
      callback: buildGroupState,
    })
  }, [customerID, userID, buildGroupState])

  React.useEffect(() => {
    window.scrollTo(0, 0)
    getGroups()
    return () => {
      AppDispatcher.dispatch({ action: 'unload-user-groups' })
    }
  }, [getGroups])

  const {
    auth: { logout },
    route: { path },
    intl: { formatMessage },
  } = props

  const toggleGroup = React.useCallback((group) => {
    const groupId = group.id ?? group.groupID
    if (!groupId || !userID) return

    const nextChecked = !group.checked
    setStatusMessageIsVisible(false)
    setStatusOutcome(undefined)
    setStatusMessage(undefined)
    setGroups((prevGroups) => prevGroups.map((item) => {
      const itemId = item.id ?? item.groupID
      if (String(itemId) !== String(groupId)) return item
      return { ...item, checked: nextChecked }
    }))

    AppDispatcher.dispatch({
      action: 'set-user-group',
      userGroup: {
        user_id: userID,
        group_id: groupId,
        is_active: nextChecked,
      },
      update: true,
      params: {
        includeGroup: true,
      },
      callback: (error) => {
        if (error) {
          setGroups((prevGroups) => prevGroups.map((item) => {
            const itemId = item.id ?? item.groupID
            if (String(itemId) !== String(groupId)) return item
            return { ...item, checked: !nextChecked }
          }))
          setStatusOutcome('failure')
          setStatusMessage(formatMessage({
            id: 'user.groups.update.failure',
            defaultMessage: 'Unable to update group permissions',
          }))
          setStatusMessageIsVisible(true)
          setTimeout(() => {
            setStatusMessageIsVisible(false)
          }, 2500)
        }
      },
    })
  }, [userID, formatMessage])

  const GROUPS = formatMessage({
    id: 'user.edit.group.permissions',
    defaultMessage: 'Group Permissions',
  })
  return (
    <div>
      <Helmet><title>{GROUPS}</title></Helmet>
      <NavigationBar
        backFunc={goBack}
        page={path}
        name={GROUPS}
        logOut={() => logout(navigate)}
      />
      {statusMessageIsVisible && (
        <StatusSave
          visible={statusMessageIsVisible}
          outcome={statusOutcome}
          failureMessage={statusMessage}
        />
      )}
      <div className={styles.pageWidth}>
        <div className={styles.pageContainer}>
          <section>
            <div className={styles.list}>
              {loading
                ? (
                  <Spinner
                    key='spinner'
                    name='double-bounce'
                    style={{ width: '30px', marginLeft: 'auto', marginRight: 'auto' }}
                  />
                  )
                : groups.map((group, index) => (
                  <AppButton
                    checked={group.checked}
                    icon={group.icon}
                    label={group.name || group.deviceGroup || group.device_group}
                    key={group.id || group.groupID || `group-${index}`}
                    onClick={() => toggleGroup(group)}
                  />
                ))}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  )
}

UserGroupsPage.propTypes = {
  auth: PropTypes.object,
  route: PropTypes.object,
  intl: PropTypes.object,
}

export default withAppStore(injectIntl(UserGroupsPage))
